/**
 * DcImport（DC Keeper 標準範本匯入）契約。
 *
 * 後端 `POST DcImport/ImportDcKeeperHardware`／`ImportDcKeeperSoftware` 收 multipart `file`
 * （DC Keeper 標準範本 xlsx）回 `ImportResultDto`。前端不解析、不重組、不驗證欄位 ——
 * 標題對欄位是後端的事，前端只把檔案送過去、把結果講清楚。
 *
 * 範本與標題列以後端提供的範本檔為準（見下方 2026-10-21 說明）。
 */

/*
 * 2026-10-21 起範本由後端提供(DcImport/Download*ImportSample,6 種),畫面「下載模板」直接下載後端檔案。
 * 下面這幾份標題列逐字照後端範本的第一列(SIT 錄在 mock/__fixtures__/import-templates.json,
 * importContract.test.js 逐欄比對);用途:mock 依欄序解析上傳檔、後端範本下載失敗時的說明。
 * `key` 只給 mock 與測試用,後端不看(後端依欄序讀)。
 */

/** 機房標準範本標題列（6 欄）。 */
export const DCKEEPER_ROOM_COLUMNS = [
  { key: "room_name", label: "機房中文名稱 (必填)" },
  { key: "address", label: "機房地址 (必填)" },
  { key: "name_english", label: "機房英文名稱 (必填)" },
  { key: "floor", label: "樓層名稱 (必填)" },
  { key: "area", label: "區域名稱 (必填)" },
  { key: "manager", label: "管理者 (使用者姓名)" },
];

/** 機櫃標準範本標題列（24 欄）。 */
export const DCKEEPER_RACK_COLUMNS = [
  { key: "room_name", label: "機房名稱 (必填)" },
  { key: "floor", label: "樓層位置 (必填)" },
  { key: "area", label: "區域位置 (必填)" },
  { key: "business_category", label: "業務類別 (必填)" },
  { key: "key_business", label: "關鍵業務 (Y/N)" },
  { key: "manager", label: "管理者 (必填) (使用者姓名)" },
  { key: "asset_name", label: "資產名稱 (必填) (Host Name)" },
  { key: "asset_category", label: "資產類別" },
  { key: "asset_no", label: "資產編號 (必填)" },
  { key: "property_no", label: "財產編號" },
  { key: "amortization_end_date", label: "結束攤提日 (必填) (yyyy-mm-dd)" },
  { key: "brand", label: "廠牌 (必填)" },
  { key: "model", label: "型號 (必填)" },
  { key: "rack_no", label: "機櫃編號 (必填)" },
  { key: "power_circuit", label: "電源迴路 (必填)" },
  { key: "voltage", label: "電壓 (必填)" },
  { key: "capacity_kva", label: "容量 KVA (必填)" },
  { key: "power_count", label: "電源數" },
  { key: "left_power_count", label: "左電源數 (必填)" },
  { key: "right_power_count", label: "右電源數 (必填)" },
  { key: "total_u", label: "總U數" },
  { key: "mounted_date", label: "上架日期 (必填) (yyyy-mm-dd)" },
  { key: "purchase_amount", label: "購買金額 (必填)" },
  { key: "description", label: "資產說明／用途" },
];

/** 硬體標準範本標題列（21 欄）。 */
export const DCKEEPER_HARDWARE_COLUMNS = [
  { key: "business_category", label: "業務類別 (必填)" },
  { key: "key_business", label: "關鍵業務 (Y/N)" },
  { key: "property_no", label: "財產編號" },
  { key: "manager", label: "管理者 (使用者姓名)" },
  { key: "host_name", label: "資產名稱 (必填) (Host Name)" },
  { key: "asset_category", label: "資產類別" },
  { key: "asset_no", label: "資產編號 (必填)" },
  { key: "brand", label: "廠牌 (必填)" },
  { key: "model", label: "型號 (必填)" },
  { key: "serial_no", label: "硬體序號 (必填)" },
  { key: "firmware_os_name", label: "韌體系統名稱" },
  { key: "firmware_os_version", label: "韌體系統版本" },
  { key: "latest_version", label: "原廠最新可用版本" },
  { key: "purchase_date", label: "購買日期 (必填) (yyyy-mm-dd)" },
  { key: "purchase_amount", label: "購買金額 (必填)" },
  { key: "mounted_date", label: "上架日期 (必填) (yyyy-mm-dd)" },
  { key: "mac_address", label: "MAC資訊" },
  { key: "os_name", label: "作業系統名稱" },
  { key: "os_version", label: "作業系統版本" },
  { key: "eos_date", label: "EOS 終止支援日" },
  { key: "eol_date", label: "EOL 生命週期終止日" },
];

/** 軟體標準範本標題列（15 欄）。 */
export const DCKEEPER_SOFTWARE_COLUMNS = [
  { key: "business_category", label: "業務類別 (必填)" },
  { key: "key_business", label: "關鍵業務 (Y/N)" },
  { key: "property_no", label: "財產編號" },
  { key: "manager", label: "管理者 (使用者姓名)" },
  { key: "host_name", label: "資產名稱 (必填) (Host Name)" },
  { key: "asset_category", label: "資產類別" },
  { key: "asset_no", label: "資產編號 (必填)" },
  { key: "brand", label: "廠牌 (必填)" },
  { key: "model", label: "型號 (必填)" },
  { key: "software_no", label: "軟體序號 (必填)" },
  { key: "version", label: "軟體版號 (必填)" },
  { key: "environment", label: "營運環境" },
  { key: "latest_version", label: "原廠最新可用版本" },
  { key: "eos_date", label: "EOS 終止支援日" },
  { key: "eol_date", label: "EOL 生命週期終止日" },
];

/** 合約資訊標準範本標題列（18 欄）。 */
export const DCKEEPER_CONTRACT_COLUMNS = [
  { key: "contract_no", label: "合約編號 (必填)" },
  { key: "name", label: "合約名稱 (必填)" },
  { key: "manager", label: "管理者 (使用者姓名)" },
  { key: "start_date", label: "合約開始時間 (必填) (yyyy-mm-dd)" },
  { key: "end_date", label: "合約結束時間 (必填) (yyyy-mm-dd)" },
  { key: "year_limit", label: "合約年限 (必填)" },
  { key: "service_level", label: "服務等級 (必填)" },
  { key: "maintenance_cycle", label: "維護週期 (必填)" },
  { key: "vendor", label: "服務廠商 (必填)" },
  { key: "amount", label: "合約金額 (必填)" },
  { key: "response_hours", label: "回覆時間" },
  { key: "on_site_hours", label: "到場時間" },
  { key: "completion_hours", label: "完修時間" },
  { key: "contact_person", label: "聯絡窗口" },
  { key: "contact_phone", label: "公司電話/分機" },
  { key: "contact_email", label: "聯絡 E-Mail" },
  { key: "primary_contract", label: "關聯主約合約（上一案）" },
  { key: "description", label: "合約內容／用途" },
];

/** IP 資訊標準範本標題列（16 欄）。 */
export const DCKEEPER_IP_COLUMNS = [
  { key: "ip_address", label: "IP資訊 (必填)" },
  { key: "parent_ip", label: "上一層 IP (必填)" },
  { key: "ip_port", label: "IP Port 位" },
  { key: "subnet_mask", label: "子網路遮罩 (必填)" },
  { key: "default_gateway", label: "預設閘道 (必填)" },
  { key: "dns", label: "DNS 伺服器" },
  { key: "mac", label: "Mac 資訊" },
  { key: "physical_port", label: "介面位置" },
  { key: "ip_manager", label: "IP 管理者 (使用者姓名)" },
  { key: "asset_name", label: "資產名稱" },
  { key: "room_name", label: "機房名稱" },
  { key: "asset_type", label: "資產類型" },
  { key: "brand", label: "廠牌" },
  { key: "model", label: "型號" },
  { key: "asset_manager", label: "管理者 (使用者姓名)" },
  { key: "asset_ip", label: "IP" },
];

/**
 * `createMissingReferences`：查無機房／機櫃／廠牌／型號／專案時要不要自動建檔。
 *
 * swagger 預設值就是 **true**（2026-09-09 確認；軟體端點註明「保留參數，本範本無需自動建檔之參照」）。
 * 樣本裡機房寫「15」、機櫃寫「2H02」，對不到就由後端自動建檔；要改政策改這一個常數就好。
 */
export const CREATE_MISSING_REFERENCES = true;

const int = (v) => (Number.isFinite(Number(v)) && v !== null && v !== "" ? Number(v) : 0);
const lines = (list) => (Array.isArray(list) ? list : [])
  .map((e) => ({ row: int(e && e.row_number), reason: (e && e.message) || "" }));

/**
 * ImportResultDto → 結果畫面。
 * `success`／`replaced` 是舊版（前端解析式匯入）結果畫面的鍵名，留別名讓元件不用分兩套。
 */
export function importResultToView(dto = {}) {
  const d = dto || {};
  const inserted = int(d.inserted_count);
  const updated = int(d.updated_count);
  return {
    total: int(d.total_rows),
    inserted,
    updated,
    // 後端的成功數自己給(success_count);缺時退回 新增＋更新
    successCount: d.success_count == null ? inserted + updated : int(d.success_count),
    failedCount: int(d.failure_count),
    success: inserted,
    replaced: updated,
    rows: lines(d.errors),
    warnings: lines(d.warnings),
  };
}
